'use client';

/**
 * IntakeWizardScroll Component
 *
 * An infinite scroll version of the intake wizard where all sections are
 * visible at once. As you scroll, the left sidebar highlights the current
 * section with status indicators:
 * - Green: completed sections (all required fields filled)
 * - Yellow: pending sections (not started or incomplete)
 * - Red: sections with validation errors
 *
 * Features:
 * - Auth checkpoints for protected sections
 * - Compliance questions support
 * - Analytics tracking (field-level and section-level)
 * - localStorage persistence for resuming
 *
 * @see Issue #471 - Form Builder Infrastructure
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { IntakeComplianceQuestion, IntakeCondition, IntakeFlowTemplate } from '@ops-matrix/shared-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { type NeonAccent, statusColors } from '../../theme/palette';
import type { TemplatePreviewData } from '../ContractSelectionCard/ContractSelectionCard';
import { StepIndicator, type StepIndicatorStep, StepStatus } from '../FormWizard';
import { GlassButton } from '../GlassButton';
import { GlassCard } from '../GlassCard';
import { AuthCheckpoint } from './components/AuthCheckpoint';
import type { ContractSelectionExtras } from './components/IntakeStepRenderer';
import { IntakeStepRenderer } from './components/IntakeStepRenderer';
import { RateCalculatorPanel } from './components/RateCalculatorPanel';
import { getI18nString } from './intake-utils';

// ============================================================================
// Types
// ============================================================================

// Analytics event types (matches IntakeWizard for consistency)
export interface FieldAnalyticEvent {
  submissionId?: string;
  sectionIndex: number;
  fieldName: string;
  fieldType: string;
  focusedAt?: string;
  completedAt?: string;
  timeSpentMs?: number;
  changeCount?: number;
  errorCount?: number;
  wasAbandoned?: boolean;
}

export interface SectionAnalyticEvent {
  submissionId?: string;
  sectionIndex: number;
  sectionName: string;
  enteredAt: string;
  exitedAt?: string;
  timeSpentMs?: number;
  completedFields: number;
  totalFields: number;
}

export interface IntakeWizardScrollProps {
  /**
   * Intake flow template
   */
  template: IntakeFlowTemplate;

  /**
   * Initial form values (for resuming)
   */
  initialValues?: Record<string, unknown>;

  /**
   * Whether user is authenticated
   */
  isAuthenticated?: boolean;

  /**
   * Callback when auth is required for a protected section
   * @param sectionIndex - The section that requires auth
   * @param formData - Current form data to preserve
   * @param authMode - Whether user wants to sign in ('login') or create account ('register')
   */
  onAuthRequired?: (sectionIndex: number, formData: Record<string, unknown>, authMode: 'login' | 'register') => void;

  /**
   * Callback when wizard is completed
   */
  onComplete: (values: Record<string, unknown>) => void | Promise<void>;

  /**
   * Callback when values change
   */
  onChange?: (values: Record<string, unknown>) => void;

  /**
   * Callback when wizard is cancelled
   */
  onCancel?: () => void;

  /**
   * Whether wizard is disabled
   */
  disabled?: boolean;

  /**
   * Whether wizard is submitting
   */
  isSubmitting?: boolean;

  /**
   * Neon accent color
   */
  neonAccent?: NeonAccent;

  /**
   * Labels for buttons
   */
  labels?: {
    submit?: string;
    cancel?: string;
  };

  /**
   * Additional class name
   */
  className?: string;

  /**
   * Scroll container max-height. Defaults to `'calc(100vh - 200px)'`, which
   * keeps the wizard self-scrolling inside an embedded surface (SplitPanel,
   * modal, sidebar). Pass any CSS height string to override the cap.
   *
   * Pass `false` to OPT OUT of the inner scroll entirely — the wizard
   * renders at its natural height and the document scrollbar takes over.
   * Use this when the wizard is the primary page content (e.g., website
   * lead-capture sections at layout='full-page'); otherwise users see two
   * scrollbars (page + inner) which is awkward and clips the form.
   */
  scrollHeight?: string | false;

  // Analytics tracking
  /**
   * Callback for field-level analytics tracking
   */
  onFieldAnalytic?: (analytic: FieldAnalyticEvent) => void;

  /**
   * Callback for section-level analytics tracking
   */
  onSectionAnalytic?: (analytic: SectionAnalyticEvent) => void;

  /**
   * Enable analytics tracking (defaults to true if callbacks provided)
   */
  analyticsEnabled?: boolean;

  /**
   * Submission ID for associating analytics
   */
  submissionId?: string;

  /**
   * localStorage key for persistence (if not provided, persistence is disabled)
   */
  storageKey?: string;

  // Dynamic options support (Business Creation)
  /**
   * Dynamic options loaded from API, keyed by dataSource
   * Used for 'dynamic-select' field types
   */
  dynamicOptions?: import('./components/IntakeStepRenderer').DynamicOptionsMap;

  /**
   * Lookup maps for display values (e.g., realmId -> realm name)
   * Used for 'summary' field types
   */
  lookupMaps?: Record<string, Record<string, string>>;

  /**
   * Issue #1382 — Optional callback to fetch an inline renderable preview
   * for a contract-package template when the user opens the card preview
   * dialog. Passed through to `IntakeStepRenderer → ContractSelectionCard`.
   */
  onFetchTemplatePreview?: (templateId: string) => Promise<TemplatePreviewData>;
  /**
   * Issue #1540 / #1533 Phase 3 — Optional bundle that turns the
   * contract-type cards into a two-level picker. When supplied, each
   * selected card renders an inline template+version dropdown.
   */
  contractSelectionExtras?: ContractSelectionExtras;

  /**
   * Optional slot rendered immediately ABOVE the submit button row, after
   * any compliance section. Used by website lead-capture forms to inject
   * TCPA/A2P SMS opt-in checkboxes per US carrier (TCR) approval rules —
   * the consent disclosure must sit directly above the submit affordance.
   */
  submitFooter?: React.ReactNode;
}

export interface IntakeWizardScrollRef {
  scrollToSection: (sectionIndex: number) => void;
  getFormData: () => Record<string, unknown>;
  /** Validate all sections. Returns index of first section with errors, or -1 if valid. */
  validate: () => number;
  clearStorage: () => void;
}

interface SectionStatus {
  status: StepStatus;
  errors: Record<string, string>;
  completedFields: number;
  totalRequiredFields: number;
  requiresAuth: boolean;
  isLocked: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const WizardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(3),
  maxWidth: 1400,
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const SidebarContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 280,
  position: 'sticky',
  top: theme.spacing(2),
  alignSelf: 'flex-start',
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    position: 'relative',
    top: 0,
    maxHeight: 'none',
  },
}));

const ScrollContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$height',
})<{ $height: string | false }>(({ theme, $height }) => ({
  flex: 1,
  minWidth: 0,
  // `$height === false` opts out of the inner-scroll cap entirely — the
  // wizard renders at its natural height and the document scrollbar takes
  // over. Use this when the wizard IS the page (e.g., website lead-capture
  // sections at layout='full-page') instead of being embedded in a
  // SplitPanel / modal where the cap keeps the sidebar visible.
  ...($height === false
    ? { maxHeight: 'none', overflowY: 'visible', overflowX: 'visible' }
    : { maxHeight: $height, overflowY: 'auto', overflowX: 'hidden' }),
  scrollBehavior: 'smooth',
  paddingRight: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: 4,
    '&:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    },
  },
}));

// Simple form container with subtle styling
const FormContainer = styled(Paper)(({ theme }) => ({
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const SectionCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isActive' && prop !== '$isLocked',
})<{ $isActive: boolean; $isLocked?: boolean }>(({ theme, $isActive, $isLocked }) => ({
  scrollMarginTop: theme.spacing(2),
  transition: 'background 0.3s ease',
  opacity: $isLocked ? 0.6 : 1,
  pointerEvents: $isLocked ? 'none' : 'auto',
  // Active section highlight
  ...($isActive && {
    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
  }),
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
}));

const SectionNumber = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$status',
})<{ $status: StepStatus }>(({ $status, theme }) => {
  const colors: Record<StepStatus, string> = {
    [StepStatus.COMPLETED]: statusColors.completed,
    [StepStatus.PENDING]: statusColors.pending,
    [StepStatus.ERROR]: statusColors.error,
    [StepStatus.CURRENT]: statusColors.active,
    [StepStatus.SKIPPED]: statusColors.inactive,
  };
  const color = colors[$status];
  const contrastText = theme.palette.getContrastText(color);

  return {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '0.875rem',
    flexShrink: 0,
    background: $status === StepStatus.COMPLETED ? color : `${color}20`,
    color: $status === StepStatus.COMPLETED ? contrastText : color,
    border: `2px solid ${color}`,
  };
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const SectionDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
}));

const LockedOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.8)',
  borderRadius: theme.shape.borderRadius,
  zIndex: 10,
  pointerEvents: 'auto', // Override parent's pointer-events: none to allow clicking the sign-in button
}));

const ComplianceSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginTop: theme.spacing(2),
  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const SecurityNote = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1, 1.5),
  marginTop: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  background: 'transparent',
  color: theme.palette.text.disabled,
  fontSize: '0.75rem',
  opacity: 0.7,
}));

// ============================================================================
// Component
// ============================================================================

export const IntakeWizardScroll = forwardRef<IntakeWizardScrollRef, IntakeWizardScrollProps>(
  (
    {
      template,
      initialValues = {},
      isAuthenticated = false,
      onAuthRequired,
      onComplete,
      onChange,
      onCancel,
      disabled = false,
      isSubmitting = false,
      neonAccent = 'primary',
      labels = {},
      className,
      scrollHeight = 'calc(100vh - 200px)',
      // Analytics
      onFieldAnalytic,
      onSectionAnalytic,
      analyticsEnabled,
      submissionId,
      // Persistence
      storageKey,
      // Dynamic options support (Business Creation)
      dynamicOptions,
      lookupMaps,
      // Issue #1382 — inline template preview
      onFetchTemplatePreview,
      // Issue #1540 — two-level contract+template picker
      contractSelectionExtras,
      submitFooter,
    },
    ref,
  ) => {
    // Load initial state from localStorage if available
    const loadPersistedData = useCallback(() => {
      if (!storageKey) return initialValues;
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          // Verify template version matches
          if (parsed.templateId === template.id && parsed.templateVersion === template.data.version) {
            return { ...initialValues, ...parsed.formData };
          }
        }
      } catch {
        // Ignore parse errors
      }
      return initialValues;
    }, [storageKey, template.id, template.data.version, initialValues]);

    // i18n
    const { t } = useI18n();

    // State
    const [formData, setFormData] = useState<Record<string, unknown>>(() => loadPersistedData());
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [activeSection, setActiveSection] = useState(0);
    const [touched, setTouched] = useState<Set<string>>(new Set());
    const [showAuthCheckpoint, setShowAuthCheckpoint] = useState(false);
    const [authInitialMode, setAuthInitialMode] = useState<'login' | 'register'>('register');
    const [pendingAuthSection, setPendingAuthSection] = useState<number | null>(null);
    const [complianceError, setComplianceError] = useState<string | null>(null);

    // Compliance question state (stored in formData with compliance_ prefix)
    const complianceQuestions = template.data.complianceQuestions || [];

    // Analytics tracking state
    const isAnalyticsEnabled = analyticsEnabled ?? Boolean(onFieldAnalytic || onSectionAnalytic);
    const sectionEnteredAtRef = useRef<Record<number, string>>({});
    const fieldFocusTimeRef = useRef<Record<string, { focusedAt: string; changeCount: number }>>({});
    const fieldErrorCountRef = useRef<Record<string, number>>({});

    // Refs
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const complianceSectionRef = useRef<HTMLDivElement | null>(null);

    // Theme
    const { breakpoints } = useTheme();
    const isCompact = useMediaQuery(breakpoints.down('md'), { noSsr: true });

    // Persist form data to localStorage
    useEffect(() => {
      if (!storageKey) return;
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            templateId: template.id,
            templateVersion: template.data.version,
            formData,
            savedAt: new Date().toISOString(),
          }),
        );
      } catch {
        // Ignore storage errors
      }
    }, [storageKey, formData, template.id, template.data.version]);

    // Clear storage helper
    const clearStorage = useCallback(() => {
      if (storageKey) {
        localStorage.removeItem(storageKey);
      }
    }, [storageKey]);

    // Evaluate conditional display for a step
    const evaluateCondition = useCallback(
      (condition: IntakeCondition | undefined): boolean => {
        if (!condition) return true;
        const fieldValue = formData[condition.field];
        const conditionValue = condition.value;

        switch (condition.operator) {
          case 'equals':
            return String(fieldValue) === String(conditionValue);
          case 'not_equals':
            return String(fieldValue) !== String(conditionValue);
          case 'contains':
            return String(fieldValue || '').includes(String(conditionValue));
          case 'greater_than':
            return Number(fieldValue) > Number(conditionValue);
          case 'less_than':
            return Number(fieldValue) < Number(conditionValue);
          case 'in':
            return Array.isArray(conditionValue) && (conditionValue as string[]).includes(String(fieldValue));
          case 'not_in':
            return Array.isArray(conditionValue) && !(conditionValue as string[]).includes(String(fieldValue));
          case 'is_empty':
            return !fieldValue || String(fieldValue).trim() === '';
          case 'is_not_empty':
            return Boolean(fieldValue) && String(fieldValue).trim() !== '';
          default:
            return true;
        }
      },
      [formData],
    );

    // Get visible steps
    const steps = useMemo(
      () => template.data.steps.filter((step) => evaluateCondition(step.conditionalDisplay)),
      [template.data.steps, evaluateCondition],
    );

    // Find first auth-required step that isn't authenticated
    const firstLockedSectionIndex = useMemo(() => {
      if (isAuthenticated) return -1;
      return steps.findIndex((step) => step.requiresAuth);
    }, [steps, isAuthenticated]);

    // Calculate section statuses
    const sectionStatuses = useMemo((): SectionStatus[] => {
      return steps.map((step, index) => {
        const stepErrors: Record<string, string> = {};
        let completedFields = 0;
        let totalRequiredFields = 0;
        const requiresAuth = step.requiresAuth || false;
        const isLocked = !isAuthenticated && requiresAuth;

        // If locked, return early with pending status
        if (isLocked) {
          return {
            status: StepStatus.PENDING,
            errors: {},
            completedFields: 0,
            totalRequiredFields: step.fields.filter((f) => f.required).length,
            requiresAuth,
            isLocked,
          };
        }

        for (const field of step.fields) {
          // Check if field is visible
          if (field.conditionalDisplay && !evaluateCondition(field.conditionalDisplay)) {
            continue;
          }

          if (field.required) {
            totalRequiredFields++;
            const value = formData[field.name];
            const hasValue = value !== undefined && value !== null && value !== '';

            if (hasValue) {
              completedFields++;
            } else if (touched.has(field.name)) {
              stepErrors[field.name] =
                `${typeof field.label === 'string' ? field.label : field.label?.default || field.name} is required`;
            }
          }

          // Check existing errors for this field
          if (errors[field.name]) {
            stepErrors[field.name] = errors[field.name];
          }
        }

        // Determine status
        let status: StepStatus;
        if (Object.keys(stepErrors).length > 0) {
          status = StepStatus.ERROR;
        } else if (completedFields === totalRequiredFields && totalRequiredFields > 0) {
          status = StepStatus.COMPLETED;
        } else if (index === activeSection) {
          status = StepStatus.CURRENT;
        } else {
          status = StepStatus.PENDING;
        }

        return {
          status,
          errors: stepErrors,
          completedFields,
          totalRequiredFields,
          requiresAuth,
          isLocked,
        };
      });
    }, [steps, formData, errors, touched, activeSection, evaluateCondition, isAuthenticated]);

    // Determine which steps should be visible (hide steps after first auth-required when unauthenticated)
    const visibleStepCount = useMemo(() => {
      if (isAuthenticated || firstLockedSectionIndex === -1) {
        return steps.length; // Show all steps if authenticated or no auth-required steps
      }
      // Show steps up to and including the first locked section
      return firstLockedSectionIndex + 1;
    }, [isAuthenticated, firstLockedSectionIndex, steps.length]);

    // Analytics: Track field focus
    const trackFieldFocus = useCallback(
      (fieldName: string) => {
        if (!isAnalyticsEnabled) return;
        fieldFocusTimeRef.current[fieldName] = {
          focusedAt: new Date().toISOString(),
          changeCount: fieldFocusTimeRef.current[fieldName]?.changeCount || 0,
        };
      },
      [isAnalyticsEnabled],
    );

    // Analytics: Track field blur
    const trackFieldBlur = useCallback(
      (fieldName: string, fieldType: string, hasValue: boolean) => {
        if (!isAnalyticsEnabled || !onFieldAnalytic) return;

        const focusInfo = fieldFocusTimeRef.current[fieldName];
        if (!focusInfo) return;

        const completedAt = new Date().toISOString();
        const focusedAt = new Date(focusInfo.focusedAt);
        const timeSpentMs = new Date(completedAt).getTime() - focusedAt.getTime();

        // Find which section this field belongs to
        const sectionIndex = steps.findIndex((step) => step.fields.some((f) => f.name === fieldName));

        onFieldAnalytic({
          submissionId,
          sectionIndex,
          fieldName,
          fieldType,
          focusedAt: focusInfo.focusedAt,
          completedAt,
          timeSpentMs,
          changeCount: focusInfo.changeCount,
          errorCount: fieldErrorCountRef.current[fieldName] || 0,
          wasAbandoned: !hasValue,
        });

        delete fieldFocusTimeRef.current[fieldName];
      },
      [isAnalyticsEnabled, onFieldAnalytic, submissionId, steps],
    );

    // Analytics: Track section entry
    useEffect(() => {
      if (!isAnalyticsEnabled) return;
      if (!sectionEnteredAtRef.current[activeSection]) {
        sectionEnteredAtRef.current[activeSection] = new Date().toISOString();
      }
    }, [activeSection, isAnalyticsEnabled]);

    // Validate a single field and return error message or null
    const validateField = useCallback(
      (name: string, value: unknown): string | null => {
        // Find the field definition across all steps
        for (const step of steps) {
          const field = step.fields.find((f) => f.name === name);
          if (!field) continue;

          // Check conditional visibility
          if (field.conditionalDisplay && !evaluateCondition(field.conditionalDisplay)) {
            return null;
          }

          // Required check
          if (field.required && (value === undefined || value === null || value === '')) {
            const label = typeof field.label === 'string' ? field.label : field.label?.default || field.name;
            return t('intake.validation.required', { field: label });
          }

          // Field-level validation (min, max, pattern)
          if (field.validation && value !== undefined && value !== null && value !== '') {
            if (field.validation.min !== undefined && typeof value === 'number') {
              if (value < field.validation.min) {
                return (
                  field.validation.messages?.min ||
                  field.validation.message ||
                  t('intake.validation.minValue', { min: field.validation.min })
                );
              }
            }
            if (field.validation.max !== undefined && typeof value === 'number') {
              if (value > field.validation.max) {
                return (
                  field.validation.messages?.max ||
                  field.validation.message ||
                  t('intake.validation.maxValue', { max: field.validation.max })
                );
              }
            }
            if (field.validation.minLength !== undefined && typeof value === 'string') {
              if (value.length < field.validation.minLength) {
                return (
                  field.validation.messages?.minLength ||
                  field.validation.message ||
                  t('intake.validation.minLength', { min: field.validation.minLength })
                );
              }
            }
            if (field.validation.pattern && typeof value === 'string') {
              const regex = new RegExp(field.validation.pattern);
              if (!regex.test(value)) {
                return (
                  field.validation.messages?.pattern || field.validation.message || t('intake.validation.invalidFormat')
                );
              }
            }
            if (field.validation.matchField && typeof value === 'string') {
              const matchValue = formData[field.validation.matchField];
              if (value !== matchValue) {
                return (
                  field.validation.messages?.matchField ||
                  field.validation.message ||
                  t('intake.validation.fieldsMustMatch')
                );
              }
            }
          }

          return null;
        }
        return null;
      },
      [steps, evaluateCondition, t],
    );

    // Update form data
    const updateFormData = useCallback(
      (name: string, value: unknown) => {
        const newData = { ...formData, [name]: value };
        setFormData(newData);
        onChange?.(newData);

        // Mark field as touched
        setTouched((prev) => new Set(prev).add(name));

        // Track field change for analytics
        if (isAnalyticsEnabled && fieldFocusTimeRef.current[name]) {
          fieldFocusTimeRef.current[name].changeCount += 1;
        }

        // Real-time validation: validate field immediately on change
        const error = validateField(name, value);
        setErrors((prev) => {
          if (error) {
            return { ...prev, [name]: error };
          }
          if (prev[name]) {
            const next = { ...prev };
            delete next[name];
            return next;
          }
          return prev;
        });
      },
      [formData, onChange, isAnalyticsEnabled, validateField],
    );

    // Handle compliance question change
    const handleComplianceChange = useCallback(
      (questionId: string, checked: boolean) => {
        updateFormData(`compliance_${questionId}`, checked);
        if (checked) {
          setComplianceError(null);
        }
      },
      [updateFormData],
    );

    // Check if a compliance question is satisfied (by checkbox OR linked form field)
    const isComplianceSatisfied = useCallback(
      (question: IntakeComplianceQuestion, data: Record<string, unknown>): boolean => {
        // Check linked form field first
        if (question.linkedField) {
          const fieldValue = data[question.linkedField];
          if (question.linkedFieldAcceptedValues) {
            if (question.linkedFieldAcceptedValues.includes(String(fieldValue))) return true;
          } else if (fieldValue) {
            return true;
          }
        }
        // Fall back to the manual compliance checkbox
        return data[`compliance_${question.id}`] === true;
      },
      [],
    );

    // Validate compliance questions
    const validateCompliance = useCallback((): boolean => {
      for (const question of complianceQuestions) {
        if (question.requiredAnswer && !isComplianceSatisfied(question, formData)) {
          setComplianceError(question.blockingMessage || 'You must acknowledge all required terms to proceed.');
          return false;
        }
      }
      setComplianceError(null);
      return true;
    }, [complianceQuestions, formData, isComplianceSatisfied]);

    // Reactively clear compliance errors as user fills in linked fields or checkboxes
    useEffect(() => {
      if (!complianceError) return;
      const allSatisfied = complianceQuestions.every((q) => !q.requiredAnswer || isComplianceSatisfied(q, formData));
      if (allSatisfied) setComplianceError(null);
    }, [formData, complianceQuestions, complianceError, isComplianceSatisfied]);

    // Compliance section status for sidebar (must be after isComplianceSatisfied)
    const complianceStatus = useMemo((): StepStatus => {
      if (complianceQuestions.length === 0 || visibleStepCount < steps.length) return StepStatus.PENDING;
      if (complianceError) return StepStatus.ERROR;
      if (complianceQuestions.every((q) => !q.requiredAnswer || isComplianceSatisfied(q, formData))) {
        return StepStatus.COMPLETED;
      }
      return StepStatus.PENDING;
    }, [complianceQuestions, complianceError, formData, isComplianceSatisfied, visibleStepCount, steps.length]);

    // Convert to StepIndicator format (visible steps + compliance section)
    const indicatorSteps: StepIndicatorStep[] = useMemo(() => {
      const formSteps = steps.slice(0, visibleStepCount).map((step, index) => ({
        id: step.id,
        title: getI18nString(step.name),
        description: getI18nString(step.description),
        optional: false,
        status: sectionStatuses[index]?.status || StepStatus.PENDING,
      }));

      // Add compliance section to sidebar when visible
      if (complianceQuestions.length > 0 && visibleStepCount === steps.length) {
        formSteps.push({
          id: 'compliance',
          title: t('intake.compliance.sidebarTitle'),
          description: t('intake.compliance.sidebarDescription'),
          optional: false,
          status: complianceStatus,
        });
      }

      return formSteps;
    }, [steps, sectionStatuses, visibleStepCount, complianceQuestions, complianceStatus]);

    // Validate all sections — returns index of first section with errors, or -1 if valid
    const validateAll = useCallback((): number => {
      const newErrors: Record<string, string> = {};
      const allTouched = new Set<string>();
      let firstErrorSection = -1;

      for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
        const step = steps[stepIndex];
        if (sectionStatuses[stepIndex]?.isLocked) continue;

        const hadErrorsBefore = Object.keys(newErrors).length;

        for (const field of step.fields) {
          if (field.conditionalDisplay && !evaluateCondition(field.conditionalDisplay)) {
            continue;
          }

          allTouched.add(field.name);

          if (field.required) {
            const value = formData[field.name];
            if (value === undefined || value === null || value === '') {
              newErrors[field.name] =
                `${typeof field.label === 'string' ? field.label : field.label?.default || field.name} is required`;
              // Track error for analytics
              fieldErrorCountRef.current[field.name] = (fieldErrorCountRef.current[field.name] || 0) + 1;
            }
          }

          // Field-level validation
          if (field.validation && formData[field.name]) {
            const value = formData[field.name];

            if (field.validation.min !== undefined && typeof value === 'number') {
              if (value < field.validation.min) {
                newErrors[field.name] =
                  field.validation.messages?.min ||
                  field.validation.message ||
                  `Minimum value is ${field.validation.min}`;
              }
            }

            if (field.validation.max !== undefined && typeof value === 'number') {
              if (value > field.validation.max) {
                newErrors[field.name] =
                  field.validation.messages?.max ||
                  field.validation.message ||
                  `Maximum value is ${field.validation.max}`;
              }
            }

            if (field.validation.minLength !== undefined && typeof value === 'string') {
              if (value.length < field.validation.minLength) {
                newErrors[field.name] =
                  field.validation.messages?.minLength ||
                  field.validation.message ||
                  `Minimum ${field.validation.minLength} characters`;
              }
            }

            if (field.validation.pattern && typeof value === 'string') {
              const regex = new RegExp(field.validation.pattern);
              if (!regex.test(value)) {
                newErrors[field.name] =
                  field.validation.messages?.pattern || field.validation.message || 'Invalid format';
              }
            }
            if (field.validation.matchField && typeof value === 'string') {
              const matchValue = formData[field.validation.matchField];
              if (value !== matchValue) {
                newErrors[field.name] =
                  field.validation.messages?.matchField || field.validation.message || 'Fields must match';
              }
            }
          }
        }

        // Track the first section that introduced errors
        if (firstErrorSection === -1 && Object.keys(newErrors).length > hadErrorsBefore) {
          firstErrorSection = stepIndex;
        }
      }

      setTouched(allTouched);
      setErrors(newErrors);
      return firstErrorSection;
    }, [steps, formData, evaluateCondition, sectionStatuses]);

    // Scroll to section (or compliance section if index === steps.length)
    const scrollToSection = useCallback(
      (index: number) => {
        if (index === steps.length && complianceSectionRef.current) {
          complianceSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        const sectionEl = sectionRefs.current[index];
        if (sectionEl && scrollContainerRef.current) {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      [steps.length],
    );

    // Handle step click from sidebar
    const handleStepClick = useCallback(
      (index: number) => {
        // Compliance section click (last sidebar item when compliance is visible)
        if (index >= visibleStepCount && complianceQuestions.length > 0) {
          scrollToSection(steps.length);
          return;
        }
        const status = sectionStatuses[index];
        if (status?.isLocked) {
          // Show auth checkpoint - let user interact with modal before redirecting
          setPendingAuthSection(index);
          setShowAuthCheckpoint(true);
          return;
        }
        scrollToSection(index);
      },
      [sectionStatuses, scrollToSection, visibleStepCount, complianceQuestions.length, steps.length],
    );

    // Handle auth checkpoint continue
    const handleAuthContinue = useCallback(() => {
      setShowAuthCheckpoint(false);
      if (pendingAuthSection !== null) {
        scrollToSection(pendingAuthSection);
        setPendingAuthSection(null);
      }
    }, [pendingAuthSection, scrollToSection]);

    // Handle submit
    const handleSubmit = useCallback(() => {
      // Check if there are locked sections
      if (firstLockedSectionIndex !== -1) {
        // Show auth checkpoint - let user interact with modal before redirecting
        setPendingAuthSection(firstLockedSectionIndex);
        setShowAuthCheckpoint(true);
        return;
      }

      // Validate all fields — returns first error section index, or -1 if valid
      const firstErrorSection = validateAll();
      if (firstErrorSection !== -1) {
        scrollToSection(firstErrorSection);
        return;
      }

      // Validate compliance (only when the compliance section is visible)
      const complianceVisible = complianceQuestions.length > 0 && visibleStepCount === steps.length;
      if (complianceVisible && !validateCompliance()) {
        scrollToSection(steps.length);
        return;
      }

      // Track final analytics
      if (isAnalyticsEnabled && onSectionAnalytic) {
        Object.entries(sectionEnteredAtRef.current).forEach(([indexStr, enteredAt]) => {
          const index = parseInt(indexStr, 10);
          const step = steps[index];
          const status = sectionStatuses[index];
          if (step && status) {
            onSectionAnalytic({
              submissionId,
              sectionIndex: index,
              sectionName: getI18nString(step.name),
              enteredAt,
              exitedAt: new Date().toISOString(),
              timeSpentMs: new Date().getTime() - new Date(enteredAt).getTime(),
              completedFields: status.completedFields,
              totalFields: status.totalRequiredFields,
            });
          }
        });
      }

      // Clear storage on successful submit
      clearStorage();

      onComplete(formData);
    }, [
      firstLockedSectionIndex,
      validateAll,
      validateCompliance,
      complianceQuestions,
      visibleStepCount,
      onComplete,
      formData,
      sectionStatuses,
      scrollToSection,
      isAnalyticsEnabled,
      onSectionAnalytic,
      steps,
      submissionId,
      clearStorage,
    ]);

    // Intersection Observer for scroll tracking
    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const observer = new IntersectionObserver(
        (entries) => {
          let maxRatio = 0;
          let mostVisibleIndex = activeSection;

          entries.forEach((entry) => {
            // Check if it's the compliance section
            if (entry.target === complianceSectionRef.current) {
              if (entry.intersectionRatio > maxRatio) {
                maxRatio = entry.intersectionRatio;
                mostVisibleIndex = steps.length; // Compliance is the index after all steps
              }
              return;
            }
            const index = sectionRefs.current.findIndex((ref) => ref === entry.target);
            if (index !== -1 && entry.intersectionRatio > maxRatio) {
              maxRatio = entry.intersectionRatio;
              mostVisibleIndex = index;
            }
          });

          if (maxRatio > 0.3) {
            setActiveSection(mostVisibleIndex);
          }
        },
        {
          root: container,
          threshold: [0, 0.25, 0.5, 0.75, 1],
          rootMargin: '-10% 0px -60% 0px',
        },
      );

      sectionRefs.current.forEach((ref) => {
        if (ref) observer.observe(ref);
      });

      // Also observe the compliance section if present
      if (complianceSectionRef.current) {
        observer.observe(complianceSectionRef.current);
      }

      return () => observer.disconnect();
    }, [steps.length, activeSection]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        scrollToSection,
        getFormData: () => formData,
        validate: validateAll,
        clearStorage,
      }),
      [scrollToSection, formData, validateAll, clearStorage],
    );

    // Button labels
    const buttonLabels = {
      submit: labels.submit || 'Submit Application',
      cancel: labels.cancel || 'Cancel',
    };

    return (
      <WizardContainer className={className}>
        {/* Auth Checkpoint Modal */}
        {showAuthCheckpoint && (
          <AuthCheckpoint
            open={showAuthCheckpoint}
            onClose={() => setShowAuthCheckpoint(false)}
            onContinue={handleAuthContinue}
            initialMode={authInitialMode}
            onSignIn={() => {
              // Only trigger auth redirect when user clicks Sign In in modal
              if (pendingAuthSection !== null) {
                onAuthRequired?.(pendingAuthSection, formData, 'login');
              }
            }}
            onCreateAccount={() => {
              // Only trigger auth redirect when user clicks Create Account in modal
              if (pendingAuthSection !== null) {
                onAuthRequired?.(pendingAuthSection, formData, 'register');
              }
            }}
            stepName={
              pendingAuthSection !== null ? getI18nString(steps[pendingAuthSection]?.name) : 'Protected Section'
            }
          />
        )}
        {/* Sidebar with Step Indicator. Hidden when the wizard has a single
            step (or none) — the rail is just visual noise on a one-section
            form like coastal-funding's pre-qualification page. The form
            container expands to fill the row. */}
        {indicatorSteps.length > 1 && (
          <SidebarContainer>
            <GlassCard sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}
              >
                {t('intake.sidebar.sections')}
              </Typography>
              <StepIndicator
                steps={indicatorSteps}
                currentStep={activeSection}
                size={isCompact ? 'small' : 'medium'}
                orientation="vertical"
                clickable={true}
                onStepClick={handleStepClick}
                neonAccent={neonAccent}
                useStatusMode={true}
              />
            </GlassCard>
          </SidebarContainer>
        )}
        {/* Main Scrollable Content */}
        <ScrollContainer ref={scrollContainerRef} $height={scrollHeight}>
          <FormContainer elevation={0}>
            {/* Only render visible steps (hide subsequent steps after first auth-required section) */}
            {steps.slice(0, visibleStepCount).map((step, index) => {
              const sectionStatus = sectionStatuses[index];
              const isRateCalculatorStep = step.id === 'step-rate-calculator';
              const isLocked = sectionStatus?.isLocked;
              const isFirst = index === 0;
              const isLastVisible = index === visibleStepCount - 1;
              const hasHiddenSteps = !isAuthenticated && firstLockedSectionIndex !== -1 && isLastVisible;

              return (
                <Box key={step.id}>
                  {/* Divider between sections */}
                  {!isFirst && <Divider />}
                  <SectionCard
                    ref={(el: HTMLDivElement | null) => {
                      sectionRefs.current[index] = el;
                    }}
                    $isActive={index === activeSection}
                    $isLocked={isLocked}
                    data-section-index={index}
                    sx={{ position: 'relative' }}
                  >
                    {/* Locked overlay for auth-required sections */}
                    {isLocked && (
                      <LockedOverlay>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            🔒 Authentication Required
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              mb: 2,
                            }}
                          >
                            Please sign in to access this section
                          </Typography>
                          {hasHiddenSteps && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                mb: 2,
                                display: 'block',
                              }}
                            >
                              {steps.length - visibleStepCount} more{' '}
                              {steps.length - visibleStepCount === 1 ? 'section' : 'sections'} will be revealed after
                              sign in
                            </Typography>
                          )}
                          <GlassButton
                            variant="gradient"
                            size="small"
                            onClick={() => {
                              setPendingAuthSection(index);
                              setShowAuthCheckpoint(true);
                              // Don't call onAuthRequired here - let user interact with modal first
                            }}
                          >
                            Sign In to Continue
                          </GlassButton>
                        </Box>
                      </LockedOverlay>
                    )}

                    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                      {/* Section Header */}
                      <SectionHeader>
                        <SectionNumber $status={sectionStatus.status}>{index + 1}</SectionNumber>
                        <Box>
                          <SectionTitle>{getI18nString(step.name)}</SectionTitle>
                          {step.description && (
                            <SectionDescription>{getI18nString(step.description)}</SectionDescription>
                          )}
                        </Box>
                      </SectionHeader>

                      {/* Section Content */}
                      {isRateCalculatorStep && template.data.rateCalculatorConfig ? (
                        <RateCalculatorPanel
                          config={template.data.rateCalculatorConfig}
                          formData={formData}
                          onChange={updateFormData}
                          errors={{ ...errors, ...sectionStatus.errors }}
                          disabled={disabled || isLocked}
                        />
                      ) : (
                        <IntakeStepRenderer
                          step={step}
                          formData={formData}
                          onChange={updateFormData}
                          errors={{ ...errors, ...sectionStatus.errors }}
                          disabled={disabled || isLocked}
                          neonAccent={neonAccent}
                          onFieldFocus={isAnalyticsEnabled ? trackFieldFocus : undefined}
                          onFieldBlur={isAnalyticsEnabled ? trackFieldBlur : undefined}
                          dynamicOptions={dynamicOptions}
                          lookupMaps={lookupMaps}
                          onFetchTemplatePreview={onFetchTemplatePreview}
                          contractSelectionExtras={contractSelectionExtras}
                        />
                      )}

                      {/* Section error summary */}
                      {sectionStatus.status === StepStatus.ERROR && Object.keys(sectionStatus.errors).length > 0 && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          Please fill in all required fields in this section.
                        </Alert>
                      )}
                    </Box>
                  </SectionCard>
                </Box>
              );
            })}

            {/* Auth CTA — show when there are hidden steps behind authentication */}
            {!isAuthenticated && firstLockedSectionIndex !== -1 && visibleStepCount < steps.length && (
              <Box>
                <Divider />
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3.5 },
                    textAlign: 'center',
                    background: (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {t('intake.authCta.title')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 3,
                    }}
                  >
                    {t('intake.authCta.description')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <GlassButton
                      variant="gradient"
                      size="large"
                      onClick={() => {
                        setPendingAuthSection(firstLockedSectionIndex);
                        setAuthInitialMode('register');
                        setShowAuthCheckpoint(true);
                      }}
                    >
                      {t('intake.authCta.createAccount')}
                    </GlassButton>
                    <GlassButton
                      variant="ghost"
                      size="medium"
                      onClick={() => {
                        setPendingAuthSection(firstLockedSectionIndex);
                        setAuthInitialMode('login');
                        setShowAuthCheckpoint(true);
                      }}
                    >
                      {t('intake.authCta.signIn')}
                    </GlassButton>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      mt: 2,
                      display: 'block',
                    }}
                  >
                    {t('intake.authCta.remainingSections', { count: steps.length - visibleStepCount })}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Compliance Questions Section - only show when all steps are visible (user authenticated or no auth-required steps) */}
            {complianceQuestions.length > 0 && visibleStepCount === steps.length && (
              <Box ref={complianceSectionRef}>
                <Divider />
                <SectionCard $isActive={false} $isLocked={false}>
                  <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <SectionHeader>
                      <SectionNumber
                        $status={
                          complianceError
                            ? StepStatus.ERROR
                            : complianceQuestions.every((q) => !q.requiredAnswer || isComplianceSatisfied(q, formData))
                              ? StepStatus.COMPLETED
                              : StepStatus.PENDING
                        }
                      >
                        {steps.length + 1}
                      </SectionNumber>
                      <Box>
                        <SectionTitle>{t('intake.compliance.sidebarTitle')}</SectionTitle>
                        <SectionDescription>{t('intake.compliance.sidebarDescription')}</SectionDescription>
                      </Box>
                    </SectionHeader>

                    <ComplianceSection>
                      {complianceQuestions.map((question: IntakeComplianceQuestion) => {
                        const satisfied = isComplianceSatisfied(question, formData);
                        const isLinkedSatisfied =
                          question.linkedField &&
                          (question.linkedFieldAcceptedValues
                            ? question.linkedFieldAcceptedValues.includes(String(formData[question.linkedField]))
                            : Boolean(formData[question.linkedField]));
                        return (
                          <Box key={question.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                            <Checkbox
                              checked={satisfied}
                              onChange={(e) => handleComplianceChange(question.id, e.target.checked)}
                              disabled={disabled || Boolean(isLinkedSatisfied)}
                              color="primary"
                              size="small"
                              sx={{ p: 0, flexShrink: 0 }}
                            />
                            <Typography variant="body2">
                              {question.question}
                              {question.requiredAnswer && !satisfied && (
                                <span style={{ color: statusColors.error }}> *</span>
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </ComplianceSection>

                    {complianceError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {complianceError}
                      </Alert>
                    )}
                  </Box>
                </SectionCard>
              </Box>
            )}

            {/* Submit Section - only show when all steps are visible */}
            {visibleStepCount === steps.length && (
              <>
                <Divider />
                <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  {submitFooter}
                  <ButtonContainer>
                    {onCancel && (
                      <GlassButton variant="ghost" onClick={onCancel} disabled={disabled || isSubmitting} size="medium">
                        {buttonLabels.cancel}
                      </GlassButton>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <GlassButton
                      variant="gradient"
                      onClick={handleSubmit}
                      disabled={disabled || isSubmitting}
                      loading={isSubmitting}
                      size="large"
                    >
                      {buttonLabels.submit}
                    </GlassButton>
                  </ButtonContainer>

                  <SecurityNote>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Your information is encrypted and secure.
                  </SecurityNote>
                </Box>
              </>
            )}
          </FormContainer>
        </ScrollContainer>
      </WizardContainer>
    );
  },
);

IntakeWizardScroll.displayName = 'IntakeWizardScroll';

export default IntakeWizardScroll;
